import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode } from 'react';
import {
  Button,
  CategoryIcon,
  EmptyState,
  PriceTag,
  Skeleton,
  Stepper,
  cartBlocked,
  cartLineNote,
  formatMoney,
  pickText,
  useToast,
} from '@aps/ui';
import { useNavigation } from '../app/navigation.js';
import { useSettings } from '../app/settings.js';
import { canOrder } from '../app/session.js';
import { ApiError, api, publicMediaUrl } from '../lib/api.js';

export function CartScreen(): ReactNode {
  const { t, locale } = useSettings();
  const { push } = useNavigation();
  const queryClient = useQueryClient();
  const toast = useToast();

  const cart = useQuery({
    queryKey: ['cart'],
    queryFn: api.cart,
    retry: false,
    enabled: canOrder(),
  });

  const setQty = useMutation({
    mutationFn: ({ itemId, qty }: { itemId: string; qty: number }) => api.setCartQty(itemId, qty),
    onSuccess: (next) => {
      queryClient.setQueryData(['cart'], next);
    },
    /* Miqdor o'zgarishi ekranda darhol ko'rinadi — muvaffaqiyatda
       xabar ORTIQCHA. Xato esa ko'rinmaydi, u aytiladi.
       Serverning matni ko'rsatiladi: "Do'konda yetarli qoldiq yo'q"
       kabi sabab mijozga aynan kerak. */
    onError: (caught: unknown) => {
      toast.show(
        caught instanceof ApiError ? caught.message_(locale) : t('toast.failed'),
        'danger',
      );
    },
  });

  const clear = useMutation({
    mutationFn: api.clearCart,
    onSuccess: (next) => {
      queryClient.setQueryData(['cart'], next);
      // Savat bo'shab qoladi va ekran "bo'sh" holatga o'tadi —
      // xabar shu o'zgarish TASODIF emasligini bildiradi.
      toast.show(t('toast.cartCleared'));
    },
    onError: (caught: unknown) => {
      toast.show(
        caught instanceof ApiError ? caught.message_(locale) : t('toast.failed'),
        'danger',
      );
    },
  });

  // Mehmon (Telegramdan tashqarida) uchun so'rov o'chirilgan: `isPending` abadiy `true` qolardi.
  if (canOrder() && cart.isPending) {
    return (
      <div className="aps-screen">
        <Skeleton height="90px" />
        <Skeleton height="90px" />
      </div>
    );
  }

  const items = cart.data?.items ?? [];

  if (items.length === 0) {
    return (
      <div className="aps-screen">
        <h1 className="aps-screen__title">{t('cart.title')}</h1>
        <EmptyState title={t('cart.empty')} hint={t('cart.emptyHint')} />
      </div>
    );
  }

  return (
    <div className="aps-screen">
      <header className="aps-screen__header">
        <h1 className="aps-screen__title">{t('cart.title')}</h1>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            clear.mutate();
          }}
        >
          {t('cart.clear')}
        </Button>
      </header>

      <div className="aps-stack">
        {items.map((item) => {
          const note = cartLineNote(item, locale);
          return (
            <div key={item.id} className="aps-cartline">
              <span className="aps-cartline__thumb">
                {item.imageUrl === null ? (
                  <span className="aps-product__fallback">
                    <CategoryIcon iconKey={item.categoryIconKey} size={32} />
                  </span>
                ) : (
                  <img
                    src={publicMediaUrl(item.imageUrl)}
                    alt=""
                    width={64}
                    height={64}
                    loading="lazy"
                  />
                )}
              </span>
              <span className="aps-shipment__body">
                <span className="aps-product__brand">{item.brandName}</span>
                <span className="aps-product__name">
                  {pickText(item.nameUz, item.nameRu, locale)}
                </span>
                {/* Pre-order tovar savatda ham muddat bilan ko'rsatiladi —
                  mijoz nimani kutayotganini bilishi kerak. IN_STOCK da qoldiq
                  yetmasa aniq aytiladi (buyurtma baribir rad etilardi). */}
                {note === null ? null : (
                  <span
                    className={`aps-stock aps-stock--${note.kind === 'pre-order' ? 'pre-order' : 'out'}`}
                  >
                    {note.text}
                  </span>
                )}
              </span>
              <span className="aps-cartline__foot">
                <span className="aps-cartline__sum">
                  <PriceTag value={item.lineTotal} locale={locale} size="sm" showDiscount={false} />
                  {item.qty > 1 ? (
                    <span className="aps-cartline__unit">
                      {formatMoney(item.unitPrice, locale)} × {item.qty}
                    </span>
                  ) : null}
                </span>
                <Stepper
                  value={item.qty}
                  min={0}
                  max={item.maxQty ?? 999}
                  label={pickText(item.nameUz, item.nameRu, locale)}
                  onChange={(qty) => {
                    setQty.mutate({ itemId: item.id, qty });
                  }}
                />
              </span>
            </div>
          );
        })}
      </div>

      <div className="aps-screen__header">
        <span className="aps-section-title">{t('cart.total')}</span>
        <span className="aps-price">
          <span className="aps-price__value">
            {formatMoney(cart.data?.itemsTotal ?? '0', locale)}
          </span>
        </span>
      </div>

      <Button
        block
        disabled={cartBlocked(items)}
        onClick={() => {
          push({ name: 'checkout' });
        }}
      >
        {t('cart.checkout')}
      </Button>
    </div>
  );
}
